// frontend/src/pages/storeActivity/SystemPanel.jsx
//
// What the system is doing for this store, as opposed to what its people
// do: whether the Shopify connection works, what the scheduler has run per
// pipeline, and what the automatic health checks flagged. The backend calls
// that last one the watchdog and its findings anomalies; the panel says
// "system health alerts", because a staff reader is not reading internals.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ICON, ArrowsClockwise, Lightning, WarningCircle } from '../../icons';
import { useFormatters } from '../../hooks/useFormatters';
import { Def, DefGroup, Panel, RelativeTime } from './primitives';
import { pipelineLabel, shopPlanReading } from './text';

export function ConnectionPanel({ profile }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const yesNo = (value) => t(value ? 'common.yes' : 'common.no');
  // A past access-token expiry is not a fault: the next call routes through
  // the refresh path. Only a stamped auth error is the store disconnected —
  // the refresh path refuses to run while it is set
  // (shopify_app/services/token_refresh.py clears it on success).
  const broken = !!profile.last_auth_error_at;
  // Who this store is, from the reading taken at its last install. Dated in
  // the label because nothing refreshes it in between.
  const plan = shopPlanReading(profile, t, formatDateTime);

  return (
    <Panel icon={<Lightning size={ICON.md} />} title={t('storeActivity.connection.title')}>
      <div className={`sa-conn${broken ? ' sa-conn--broken' : ''}`}>
        {broken
          ? t('storeActivity.connection.authError', { when: formatDateTime(profile.last_auth_error_at) })
          : t('storeActivity.connection.ok')}
      </div>
      <DefGroup>
        <Def
          label={t('storeActivity.connection.plan')}
          value={plan.label}
          warn={plan.isDevelopment}
          wide
        />
        <Def
          label={t('storeActivity.connection.lastSync')}
          value={profile.last_sync ? formatDateTime(profile.last_sync) : t('storeActivity.connection.never')}
        />
        <Def label={t('storeActivity.connection.ordersSynced')} value={yesNo(profile.orders_synced)} />
        <Def label={t('storeActivity.connection.forecastsGenerated')} value={yesNo(profile.forecasts_generated)} />
      </DefGroup>
    </Panel>
  );
}

export function AutomationPanel({ automation, windowDays }) {
  const { t } = useTranslation();
  return (
    <Panel
      icon={<ArrowsClockwise size={ICON.md} />}
      title={t('storeActivity.automation.title')}
      subtitle={t('storeActivity.automation.subtitle')}
    >
      {automation.length === 0
        ? <div className="sa-panel-empty">{t('storeActivity.automation.empty')}</div>
        : (
          <div className="sa-list">
            {automation.map((run) => (
              <div className="sa-list-row" key={run.pipeline}>
                <div className="sa-list-main">
                  <div className="sa-list-primary">{pipelineLabel(run.pipeline, t)}</div>
                  <div className="sa-list-secondary">
                    {t('storeActivity.automation.runsWindow', { count: run.runs_window, days: windowDays })}
                    {run.failed_window > 0 && (
                      <span className="sa-badge sa-badge--warn">
                        {t('storeActivity.automation.failedWindow', { count: run.failed_window })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="sa-list-end">
                  <span className={`sa-badge${run.last_status === 'succeeded' ? '' : ' sa-badge--warn'}`}>
                    {t(`storeActivity.automation.status.${run.last_status}`)}
                  </span>
                  <RelativeTime className="sa-list-secondary" value={run.last_run} />
                </div>
              </div>
            ))}
          </div>
        )}
    </Panel>
  );
}

export function AnomaliesPanel({ anomalies, truncated }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  return (
    <Panel
      icon={<WarningCircle size={ICON.md} />}
      title={t('storeActivity.anomalies.title')}
      subtitle={t('storeActivity.anomalies.subtitle')}
      count={anomalies.length}
    >
      {anomalies.length === 0
        ? <div className="sa-panel-empty">{t('storeActivity.empty.anomalies')}</div>
        : (
          <div className="sa-list">
            {anomalies.map((a, i) => (
              <div className="sa-list-row" key={i}>
                <div className="sa-list-main">
                  {/* What the check found, as a sentence — this reader is
                      deciding whether to act, and a bare category id is not a
                      decision. No defaultValue: the category is a closed
                      vocabulary (core/health_anomalies.py, pinned by
                      api/test_i18n_dynamic_key_contracts.py), so every
                      category the code can raise ships with its sentence. */}
                  <div className="sa-list-primary sa-list-primary--wrap">
                    {t(`storeActivity.anomalies.category.${a.category}`)}
                  </div>
                  {/* The raw id stays visible under it: it is what the Django
                      admin list filters the same finding by (backend
                      core/admin.py HealthAnomalyAdmin), and what you grep for
                      in core/health_anomalies.py. */}
                  <div className="sa-list-secondary">
                    <code className="sa-code">{a.category}</code>
                    {` · ${formatDateTime(a.detected_at)}`}
                  </div>
                </div>
                <span className={`sa-badge${a.severity === 'info' ? '' : ' sa-badge--warn'}`}>
                  {t(`storeActivity.anomalies.severity.${a.severity}`)}
                </span>
              </div>
            ))}
          </div>
        )}
      {/* A capped list says so, same as the timeline: the backend computes
          the flag, and a capped list rendered without its note reads as the
          store's whole history. */}
      {truncated && (
        <div className="sa-panel-note">{t('storeActivity.truncated.anomalies')}</div>
      )}
    </Panel>
  );
}
